import React, { useState, useEffect } from 'react';
import { Button, Heading, Stack, Text, Box } from '@chakra-ui/react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext'; // Asegúrate de importar correctamente
import { validarCoordenadasParaMapa } from '../../utils/coordenadas';

// Icono taxi para el destino
const taxiIcon = new L.Icon({
  iconUrl: '/assets/images/taxi-marker.png',
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48],
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
});

// Fix para el icono de Leaflet por defecto (recogida)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Componente auxiliar para ajustar el mapa a los puntos
function FitBounds({ puntos }) {
  const map = useMap();
  useEffect(() => {
    // Filtrar puntos válidos (que no sean null, undefined o sin coordenadas)
    const puntosValidos = puntos.filter(p => p && Array.isArray(p) && p.length === 2 && !isNaN(p[0]) && !isNaN(p[1]));
    
    if (puntosValidos.length === 0) return;
    
    if (puntosValidos.length === 1) {
      map.setView(puntosValidos[0], 15);
    } else {
      const bounds = L.latLngBounds(puntosValidos);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [puntos, map]);
  return null;
}

// Función para formatear la fecha y hora a formato español y zona Madrid, con mes en tres letras
function formatFechaHora(fechaHoraUTC) {
  if (!fechaHoraUTC) return { fecha: '', hora: '' };
  const fecha = new Date(fechaHoraUTC);
  const opciones = {
    timeZone: 'Europe/Madrid',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  const formateado = fecha.toLocaleString('es-ES', opciones);
  const [fechaStr, horaStr] = formateado.split(',').map(s => s.trim());
  const fechaCapitalizada = fechaStr.replace(
    /(\d{2}) (\w{3}) (\d{4})/,
    (_, d, m, y) => `${d} ${m.charAt(0).toUpperCase() + m.slice(1)} ${y}`
  );
  return { fecha: fechaCapitalizada, hora: horaStr };
}

// Función para obtener el código postal a partir de coordenadas usando tu backend
async function obtenerCodigoPostal(lat, lng) {
  // Elimina cualquier espacio al construir la URL
  const url = `${process.env.REACT_APP_API_BASE_URL}/api/maps/geocode?latlng=${lat},${lng}`;
  console.log("[obtenerCodigoPostal] Fetching:", url);
  const resp = await fetch(url);
  console.log("[obtenerCodigoPostal] Status:", resp.status);
  if (!resp.ok) {
    const errorText = await resp.text();
    console.error("[obtenerCodigoPostal] Error en geocode backend:", resp.status, errorText);
    return "";
  }
  const data = await resp.json();
  console.log("[obtenerCodigoPostal] Respuesta geocode backend:", data);
  const postal = data.results
    .flatMap(r => r.address_components)
    .find(c => c.types.includes("postal_code"));
  console.log("[obtenerCodigoPostal] Postal encontrado:", postal ? postal.long_name : "Ninguno");
  return postal ? postal.long_name : "";
}

// Función para obtener ruta y distancia usando tu backend
async function obtenerRutaYDistancia(origen, destino) {
  const url = `${process.env.REACT_APP_API_BASE_URL}/api/maps/directions?origin=${origen[0]},${origen[1]}&destination=${destino[0]},${destino[1]}&mode=driving`.replace(/ /g, '');
  console.log("[obtenerRutaYDistancia] Fetching:", url);
  const resp = await fetch(url);
  console.log("[obtenerRutaYDistancia] Status:", resp.status);
  if (!resp.ok) {
    const errorText = await resp.text();
    console.error("[obtenerRutaYDistancia] Error en directions backend:", resp.status, errorText);
    return null;
  }
  const data = await resp.json();
  console.log("[obtenerRutaYDistancia] Respuesta directions backend:", data);
  if (data.routes && data.routes.length > 0) {
    const leg = data.routes[0].legs[0];
    const resultado = {
      distancia_km: leg.distance.value / 1000,
      duracion_seg: leg.duration.value,
      fin_estimado: leg.duration.value,
    };
    console.log("[obtenerRutaYDistancia] Resultado:", resultado);
    return resultado;
  }
  console.warn("[obtenerRutaYDistancia] No se encontraron rutas.");
  return null;
}

// Nueva función para estimar el precio basado en la distancia
function estimarPrecio(distancia_km) {
  return 2 + distancia_km * 1;
}

// Nueva función para estimar la fecha y hora de fin
function estimarFechaHoraFin(fecha_hora_inicio, duracion_seg) {
  console.log("[estimarFechaHoraFin] Entrada - fecha_hora_inicio:", fecha_hora_inicio);
  console.log("[estimarFechaHoraFin] Entrada - duracion_seg:", duracion_seg);
  
  if (!fecha_hora_inicio || isNaN(new Date(fecha_hora_inicio).getTime())) {
    console.error("[estimarFechaHoraFin] fecha_hora_inicio no es válida:", fecha_hora_inicio);
    return null; // Retornar null si la fecha de inicio no es válida
  }
  if (!duracion_seg || isNaN(duracion_seg)) {
    console.error("[estimarFechaHoraFin] duracion_seg no es válida:", duracion_seg);
    return null; // Retornar null si la duración no es válida
  }
  
  const inicio = new Date(fecha_hora_inicio);
  const inicioTimestamp = inicio.getTime();
  const duracionMs = duracion_seg * 1000;
  const finTimestamp = inicioTimestamp + duracionMs;
  const fin = new Date(finTimestamp);
  const finISO = fin.toISOString();
  
  console.log("[estimarFechaHoraFin] inicio Date:", inicio);
  console.log("[estimarFechaHoraFin] inicio timestamp:", inicioTimestamp);
  console.log("[estimarFechaHoraFin] duracion en ms:", duracionMs);
  console.log("[estimarFechaHoraFin] fin timestamp:", finTimestamp);
  console.log("[estimarFechaHoraFin] fin Date:", fin);
  console.log("[estimarFechaHoraFin] fin ISO:", finISO);
  
  return finISO;
}

async function completarDatosReserva(datosReserva) {
  console.log("[completarDatosReserva] Iniciando con datosReserva:", datosReserva);
  if (!datosReserva.fecha_hora_inicio) {
    console.error("[completarDatosReserva] fecha_hora_inicio no está definida:", datosReserva);
    return datosReserva; // Retornar los datos originales si fecha_hora_inicio no está definida
  }

  const [latO, lngO] = datosReserva.puntoRecogida || [];
  const [latD, lngD] = datosReserva.puntoDestino || [];

  // Validar coordenadas
  if (!latO || !lngO || !latD || !lngD) {
    console.error("[completarDatosReserva] Coordenadas inválidas:", datosReserva);
    return datosReserva; // Retornar los datos originales si las coordenadas son inválidas
  }

  try {
    // 1. Códigos postales
    const origen_codigo_postal = await obtenerCodigoPostal(latO, lngO);
    const destino_codigo_postal = await obtenerCodigoPostal(latD, lngD);

    // 2. Ruta y distancia
    const ruta = await obtenerRutaYDistancia([latO, lngO], [latD, lngD]);

    // 3. Precio
    const precio = ruta ? estimarPrecio(ruta.distancia_km) : null;

    // 4. Fecha/hora fin
    const fecha_hora_fin = ruta
      ? estimarFechaHoraFin(datosReserva.fecha_hora_inicio, ruta.duracion_seg)
      : null;

    const resultado = {
      ...datosReserva,
      origen_codigo_postal,
      destino_codigo_postal,
      precio,
      distancia_km: ruta ? ruta.distancia_km : null,
      fecha_hora_fin,
    };
    console.log("[completarDatosReserva] Resultado final:", resultado);
    return resultado;
  } catch (error) {
    console.error("[completarDatosReserva] Error:", error);
    return datosReserva; // Retornar los datos originales en caso de error
  }
}

const FasePago = ({ onNext, datosReserva }) => {
  const [estado, setEstado] = useState('pendiente'); // 'pendiente', 'procesando', 'ok', 'error'
  const [datosCompletos, setDatosCompletos] = useState(datosReserva); // Estado para los datos completos de la reserva
  const [cargandoDatos, setCargandoDatos] = useState(true); // Estado para saber si estamos cargando datos
  const [configuraciones, setConfiguraciones] = useState(null);
  const { t, i18n } = useTranslation();
  const { user, accessToken, getEffectiveUser } = useAuth();
  
  // Obtener usuario efectivo (autenticado o anónimo)
  const effectiveUser = getEffectiveUser();
  
  // Detectar si estamos en entorno de pruebas (localhost)
  const isTestEnvironment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Cargar configuraciones al montar el componente
  useEffect(() => {
    const cargarConfiguraciones = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/settings/public/configuraciones-pago`
        );
        
        if (response.ok) {
          const data = await response.json();
          setConfiguraciones(data);
        }
      } catch (error) {
        console.error('[FasePago] Error cargando configuraciones:', error);
      }
    };
    
    cargarConfiguraciones();
  }, []);

  // Completar datos al montar el componente
  useEffect(() => {
    const cargarDatos = async () => {
      console.log("[FasePago] Completando datos de reserva...");
      console.log("[FasePago] datosReserva recibidos:", datosReserva);
      setCargandoDatos(true);
      
      // Si tenemos reserva_id pero no tenemos los puntos, recuperar del backend
      if (datosReserva.reserva_id && (!datosReserva.puntoRecogida || !datosReserva.puntoDestino)) {
        console.log("[FasePago] Detectado reserva_id sin datos completos. Recuperando del backend...");
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/reservas/${datosReserva.reserva_id}`,
            {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.ok) {
            const reservaCompleta = await response.json();
            console.log("[FasePago] Reserva recuperada del backend:", reservaCompleta);
            
            // Parsear origen y destino de formato "lat,lng" a [lat, lng]
            let puntoRecogida = reservaCompleta.origen ? 
              reservaCompleta.origen.split(',').map(Number) : null;
            let puntoDestino = reservaCompleta.destino ? 
              reservaCompleta.destino.split(',').map(Number) : null;
            
            // Validar y corregir coordenadas automáticamente si están invertidas
            if (puntoRecogida && puntoRecogida.length === 2) {
              const [lat, lng] = puntoRecogida;
              const resultado = validarCoordenadasParaMapa(lat, lng, "Punto de recogida");
              if (resultado.validas) {
                puntoRecogida = [resultado.lat, resultado.lng];
              } else {
                console.error("[FasePago] Punto de recogida con coordenadas inválidas, descartado");
                puntoRecogida = null;
              }
            }
            
            if (puntoDestino && puntoDestino.length === 2) {
              const [lat, lng] = puntoDestino;
              const resultado = validarCoordenadasParaMapa(lat, lng, "Punto de destino");
              if (resultado.validas) {
                puntoDestino = [resultado.lat, resultado.lng];
              } else {
                console.error("[FasePago] Punto de destino con coordenadas inválidas, descartado");
                puntoDestino = null;
              }
            }
            
            const datosRecuperados = {
              ...datosReserva,
              tipoServicio: reservaCompleta.tipo,
              puntoRecogida,
              puntoDestino,
              fecha_hora_inicio: reservaCompleta.fecha_hora_inicio,
              fecha_hora_fin: reservaCompleta.fecha_hora_fin,
              origen_codigo_postal: reservaCompleta.origen_codigo_postal,
              destino_codigo_postal: reservaCompleta.destino_codigo_postal,
              origen_direccion: reservaCompleta.origen_direccion,
              destino_direccion: reservaCompleta.destino_direccion,
              precio: reservaCompleta.precio,
              distancia_km: reservaCompleta.distancia_km
            };
            
            console.log("[FasePago] Datos recuperados y parseados:", datosRecuperados);
            setDatosCompletos(datosRecuperados);
            setCargandoDatos(false);
            return;
          } else {
            console.error("[FasePago] Error al recuperar reserva:", response.status);
          }
        } catch (error) {
          console.error("[FasePago] Error al recuperar reserva:", error);
        }
      }
      
      // Si no hay reserva_id o ya tenemos los datos, completar normalmente
      const datosActualizados = await completarDatosReserva(datosReserva);
      console.log("[FasePago] Datos completados:", datosActualizados);
      setDatosCompletos(datosActualizados);
      setCargandoDatos(false);
    };
    
    cargarDatos();
  }, [datosReserva, accessToken]);

  useEffect(() => {
    console.log("[FasePago] JSON de datosReserva:", JSON.stringify(datosReserva, null, 2)); // Depuración
    console.log("[FasePago] JSON de datosCompletos:", JSON.stringify(datosCompletos, null, 2)); // Depuración
    console.log("[FasePago] JSON de datos de autenticación del usuario:", JSON.stringify(user, null, 2)); // Depuración
    console.log("[FasePago] Usuario efectivo:", effectiveUser);
    console.log("[FasePago] Es anónimo:", effectiveUser?.isAnonymous);
  }, [datosReserva, datosCompletos, user, effectiveUser]);

  const handlePago = async (medioPago) => {
    console.log("[FasePago] Iniciando pago con medio:", medioPago);
    setEstado('procesando');
    try {
      const metodo = medioPago === "tarjeta" ? "C" : "z";

      // Verificar si ya existe una reserva (reintento de pago)
      const reservaExistente = datosReserva.reserva_id || datosReserva.reservaId || datosCompletos.reserva_id;
      
      if (reservaExistente) {
        console.log("[FasePago] ⚠️ Detectada reserva existente:", reservaExistente);
        console.log("[FasePago] Redirigiendo a endpoint de reintento de pago...");
        
        // Usar endpoint de reintento de pago
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/pagos/reintentar/${reservaExistente}?metodo=${metodo}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            }
          }
        );
        
        if (!response.ok) {
          console.error("[FasePago] Error al reintentar pago:", response.status, response.statusText);
          const errorData = await response.json().catch(() => ({ detail: 'Error desconocido' }));
          console.error("[FasePago] Detalles del error:", errorData);
          alert(`Error al reintentar el pago: ${errorData.detail || response.statusText}`);
          setEstado('error');
          return;
        }
        
        const data = await response.json();
        console.log("[FasePago] Respuesta de reintento:", JSON.stringify(data, null, 2));
        
        // Crear y enviar formulario POST a la pasarela
        if (data.url_pasarela && data.form_params) {
          console.log("[FasePago] Creando formulario POST para Redsys (reintento):", data.url_pasarela);
          
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = data.url_pasarela;
          
          Object.keys(data.form_params).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = data.form_params[key];
            form.appendChild(input);
          });
          
          document.body.appendChild(form);
          console.log("[FasePago] Enviando formulario POST a Redsys (reintento)...");
          form.submit();
          return;
        }
        
        console.error("[FasePago] Respuesta de reintento sin url_pasarela");
        setEstado('error');
        return;
      }

      // Si no hay reserva existente, proceder con la creación normal
      // Usar datos completados
      const origen = datosCompletos.puntoRecogida ? datosCompletos.puntoRecogida.join(",") : "";
      const destino = datosCompletos.puntoDestino ? datosCompletos.puntoDestino.join(",") : "";

      console.log("[FasePago] VERIFICACIÓN DE FECHAS:");
      console.log("  - fecha_hora_inicio (entrada):", datosCompletos.fecha_hora_inicio);
      console.log("  - fecha_hora_fin (calculada):", datosCompletos.fecha_hora_fin);

      // Validar cliente_id usando usuario efectivo
      if (!effectiveUser?.user_id) {
        console.error("[FasePago] Error: No hay usuario disponible (ni autenticado ni anónimo).");
        setEstado('error');
        return;
      }

      console.log("[FasePago] Usando usuario:", {
        user_id: effectiveUser.user_id,
        email: effectiveUser.email,
        isAnonymous: effectiveUser.isAnonymous
      });

      // Función para capturar información del dispositivo
      const getDeviceInfo = () => {
        let customDeviceId = localStorage.getItem('customDeviceId');
        if (!customDeviceId) {
          customDeviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
          localStorage.setItem('customDeviceId', customDeviceId);
        }

        return {
          user_agent: navigator.userAgent || null,
          accept_language: navigator.language || null,
          app_version: navigator.appVersion || null,
          platform: navigator.platform || null,
          vendor: navigator.vendor || null,
          origin: window.location.origin,
          referer: document.referrer || null,
          connection_type: navigator.connection ? navigator.connection.effectiveType : null,
          x_requested_with: "XMLHttpRequest",
          custom_device_id: customDeviceId,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          screen_color_depth: window.screen.colorDepth,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          languages: navigator.languages ? navigator.languages.join(', ') : navigator.language,
          online: navigator.onLine,
          cookie_enabled: navigator.cookieEnabled,
          do_not_track: navigator.doNotTrack,
          hardware_concurrency: navigator.hardwareConcurrency || null,
          device_memory: navigator.deviceMemory || null,
          max_touch_points: navigator.maxTouchPoints || 0,
          latitude: null,
          longitude: null,
          location_accuracy: null,
          address: null
        };
      };

      // Guardar dispositivo en el backend y obtener su UUID
      const guardarDispositivoYObtenerUUID = async () => {
        try {
          const device_info = getDeviceInfo();
          const baseURL = process.env.REACT_APP_API_BASE_URL;
          const token = localStorage.getItem('auth-token');
          
          // Enviar device_info al backend usando el endpoint de dispositivos
          const response = await fetch(`${baseURL}/api/dispositivos/guardar`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: JSON.stringify({
              device_info,
              user_id: effectiveUser.user_id
            })
          });

          if (response.ok) {
            const data = await response.json();
            return data.dispositivo_id; // El backend devuelve el UUID del dispositivo
          } else {
            console.error('Error al guardar dispositivo:', response.statusText);
            return null;
          }
        } catch (error) {
          console.error('Error obteniendo dispositivo_cliente:', error);
          return null;
        }
      };

      // Obtener el UUID del dispositivo
      const dispositivo_uuid = await guardarDispositivoYObtenerUUID();

      // Filtrar y completar los campos requeridos por el backend
      const reservaConFormaPago = {
        cliente_id: effectiveUser.user_id, // ID del usuario (autenticado o anónimo)
        conductor_id: datosCompletos.conductor_id || null, // Si aplica
        chat_id: datosCompletos.chat_id || null, // Si aplica
        estado: "inactivo", // Las reservas con pago pendiente empiezan como inactivas
        sub_estado: "pendiente_pago", // Valor válido esperado por el backend
        tipo: datosCompletos.tipoServicio,
        origen_codigo_postal: datosCompletos.origen_codigo_postal,
        destino_codigo_postal: datosCompletos.destino_codigo_postal,
        precio: datosCompletos.precio,
        distancia_km: datosCompletos.distancia_km,
        fecha_hora_inicio: datosCompletos.fecha_hora_inicio,
        fecha_hora_fin: datosCompletos.fecha_hora_fin,
        idioma: i18n.language || 'es', // Capturar el idioma actual
        dispositivo_cliente: dispositivo_uuid, // UUID del dispositivo desde la tabla dispositivos
        origen,
        destino,
        origen_direccion: datosCompletos.origen_direccion || null,
        destino_direccion: datosCompletos.destino_direccion || null,
        forma_pago: medioPago,
      };

      console.log("[FasePago] JSON de reserva con forma de pago:", JSON.stringify(reservaConFormaPago, null, 2)); // Depuración
      console.log("[FasePago] COMPARACIÓN FECHAS FINAL:");
      console.log("  - fecha_hora_inicio:", reservaConFormaPago.fecha_hora_inicio);
      console.log("  - fecha_hora_fin:", reservaConFormaPago.fecha_hora_fin);
      const inicio = new Date(reservaConFormaPago.fecha_hora_inicio);
      const fin = new Date(reservaConFormaPago.fecha_hora_fin);
      const diferencia_min = (fin - inicio) / 1000 / 60;
      console.log("  - Diferencia en minutos:", diferencia_min);
      if (diferencia_min < 0) {
        console.error("  ⚠️ ERROR: fecha_hora_fin es anterior a fecha_hora_inicio!");
      }

      // Agregado: Log de la consulta al backend
      console.log("[FasePago] Consulta al backend:", {
        url: `${process.env.REACT_APP_API_BASE_URL}/api/pagos/iniciar/?metodo=${metodo}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: reservaConFormaPago
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/pagos/iniciar/?metodo=${metodo}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(reservaConFormaPago),
      });

      // Verificar si la respuesta fue exitosa
      if (!response.ok) {
        console.error("[FasePago] Error en la respuesta del servidor:", response.status, response.statusText);
        const errorData = await response.json().catch(() => ({ detail: 'Error desconocido' }));
        console.error("[FasePago] Detalles del error:", errorData);
        alert(`Error al iniciar el pago: ${errorData.detail || response.statusText}`);
        setEstado('error');
        return;
      }

      const data = await response.json();
      console.log("[FasePago] Respuesta del servidor:", JSON.stringify(data, null, 2)); // Depuración

      // Si el backend devuelve url_pasarela y form_params, crear formulario POST
      if (data.url_pasarela && data.form_params) {
        console.log("[FasePago] Creando formulario POST para Redsys:", data.url_pasarela);
        
        // Crear formulario dinámico
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.url_pasarela;
        
        // Agregar campos del formulario
        Object.keys(data.form_params).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = data.form_params[key];
          form.appendChild(input);
        });
        
        // Agregar al DOM y enviar
        document.body.appendChild(form);
        console.log("[FasePago] Enviando formulario POST a Redsys...");
        form.submit();
        return; // Detener ejecución ya que se está redirigiendo
      }
      
      // Compatibilidad con URL GET (antiguo método, ya no debería usarse)
      if (data.url_pasarela) {
        console.log("[FasePago] Redirigiendo a pasarela de pago (GET):", data.url_pasarela);
        window.location.href = data.url_pasarela;
        return; // Detener ejecución ya que se está redirigiendo
      }
      
      // Si no hay url_pasarela pero el pago se completó directamente
      if (data.reserva_id && data.estado) {
        console.log("[FasePago] Pago completado con éxito sin pasarela. Llamando a onNext...");
        setEstado('ok'); // Actualizar estado a 'ok'
        onNext({ reserva_id: data.reserva_id, estado: data.estado }); // Propagar datos al componente padre
      } else {
        console.error("[FasePago] Datos incompletos recibidos del servidor:", JSON.stringify(data, null, 2)); // Depuración adicional
        alert('El servidor devolvió una respuesta inesperada. Por favor, verifica la consola para más detalles.');
        setEstado('error');
      }
    } catch (e) {
      console.error("[FasePago] Error en el pago:", e);
      setEstado('error');
    }
  };

  return (
    <Stack spacing={6} align="center">
      <Heading size="md">{t('pago_titulo')}</Heading>

      {/* Texto informativo sobre el pago */}
      <Box
        textAlign="center"
        fontSize="sm"
        px={4}
        py={3}
        bg="blue.50"
        borderRadius="md"
        borderWidth={1}
        borderColor="blue.200"
      >
        <Text fontWeight="bold" color="blue.800">
          {configuraciones && t('pago_informativo', {
            comision: configuraciones.comision_por_no_estar_registrado?.toFixed(2),
            precio_reserva: configuraciones.precio_reserva?.toFixed(2),
            horas_cancelacion: Math.floor(configuraciones.periodo_cancelacion_expirado / 60)
          })}
        </Text>
      </Box>

      {/* Alinear los botones horizontalmente */}
      <Stack direction="row" spacing={4} align="center">
        <Button 
          colorScheme="blue" 
          onClick={() => handlePago('bizum')}
          isDisabled={estado === 'procesando' || isTestEnvironment}
          isLoading={estado === 'procesando'}
          opacity={isTestEnvironment ? 0.5 : 1}
          cursor={isTestEnvironment ? 'not-allowed' : 'pointer'}
          title={isTestEnvironment ? t('pago_bizum_no_disponible_test') || 'Bizum no disponible en entorno de pruebas' : ''}
        >
          {t('pago_bizum')}
        </Button>
        <Button 
          colorScheme="teal" 
          onClick={() => handlePago('tarjeta')}
          isDisabled={estado === 'procesando'}
          isLoading={estado === 'procesando'}
        >
          {t('pago_tarjeta')}
        </Button>
      </Stack>
      
      {/* Mensaje de procesamiento */}
      {estado === 'procesando' && (
        <Text color="blue.600" fontSize="sm" fontWeight="600">
          {t('pago_procesando')}
        </Text>
      )}
      
      {/* Datos del usuario logado */}
      {!effectiveUser?.isAnonymous && (
        <Box w="100%" p={4} borderWidth={1} borderRadius="md" bg="black" color="white">
          <Heading size="sm" mb={2}>{t('datos_usuario_titulo')}</Heading>
          <Text><strong>{t('tipo_usuario')}:</strong> {effectiveUser?.user_type || t('desconocido')}</Text>
          <Text><strong>{t('email')}:</strong> {effectiveUser?.email || t('desconocido')}</Text>
        </Box>
      )}

      {/* Datos de la reserva */}
      <Box w="100%" p={4} borderWidth={1} borderRadius="md" bg="black" color="white">
        <Heading size="sm" mb={2}>{t('datos_reserva_titulo')}</Heading>
        <Text><strong>{t('tipo_servicio')}:</strong> {datosCompletos.tipoServicio}</Text>
        <Text><strong>{t('fecha')}:</strong> {formatFechaHora(datosCompletos.fecha_hora_inicio).fecha}</Text>
        <Text><strong>{t('hora')}:</strong> {formatFechaHora(datosCompletos.fecha_hora_inicio).hora}</Text>
        {datosCompletos.distancia_km && (
          <Text><strong>{t('distancia')}:</strong> {datosCompletos.distancia_km.toFixed(2)} km</Text>
        )}
        {datosCompletos.precio && (
          <Text><strong>{t('precio_estimado')}:</strong> {datosCompletos.precio.toFixed(2)} €</Text>
        )}
      </Box>

      {/* Mapa con puntos de recogida y destino */}
      {cargandoDatos ? (
        <Box w="100%" h="300px" borderWidth={1} borderRadius="md" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
          <Text>{t('cargando_mapa') || 'Cargando mapa...'}</Text>
        </Box>
      ) : (
        <Box w="100%" h="300px" borderWidth={1} borderRadius="md" bg="gray.100">
          <MapContainer style={{ height: "100%", width: "100%" }} zoom={13} scrollWheelZoom={false}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {/* Punto de recogida */}
            {datosCompletos.puntoRecogida && (
              <Marker position={datosCompletos.puntoRecogida}>
                <Popup>{t('direccion_recogida')}</Popup>
              </Marker>
            )}
            {/* Punto de destino */}
            {datosCompletos.puntoDestino && (
              <Marker position={datosCompletos.puntoDestino} icon={taxiIcon}>
                <Popup>{t('direccion_destino')}</Popup>
              </Marker>
            )}
            {/* Ajustar la vista para mostrar ambos puntos */}
            <FitBounds puntos={[datosCompletos.puntoRecogida, datosCompletos.puntoDestino].filter(Boolean)} />
          </MapContainer>
        </Box>
      )}

    </Stack>
  );
};

export default FasePago;
